import { pbQuery } from "@sergio9929/pb-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircleIcon, SaveIcon, TrashIcon, UndoIcon } from "lucide-react";
import { memo, useCallback, useEffect } from "react";
import { toast } from "sonner";
import proceduresLegend from "@/assets/procedure-images/procedures-legend.jpeg";
import { Odontogram } from "@/components/admin/odontogram/Odontogram";
import { useOdontogramActions } from "@/components/admin/odontogram/odontogramStore";
import { ProcedureSelector } from "@/components/admin/odontogram/ProcedureSelector";
import type { Teeth } from "@/components/admin/odontogram/types";
import { ErrorFallback, LoadingFallback } from "@/components/fallbacks";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import type { SafeOmit } from "@/lib/utils";
import { proceduresQueries } from "@/modules/procedures/queries";
import { Collections, type ProceduresResponse, type ProceduresTypesResponse, type Update } from "@/types/pocketbase.ts";

export const Route = createFileRoute("/dashboard/patients/$patientId/procedures/$procedureId")({
	component: RouteComponent,
});

function RouteComponent() {
	const { procedureId } = Route.useParams();

	const procedure = useQuery(
		proceduresQueries.one<unknown, OdontogramTabsProps["odontogram"]>({
			id: procedureId,
		})
	);

	if (procedure.isLoading) {
		return <LoadingFallback />;
	}

	if (!procedure.data) {
		return <ErrorFallback title="Procedure not found" />;
	}

	if (procedure.error) {
		return <ErrorFallback title="Error loading procedure" />;
	}

	return (
		<div>
			<OdontogramTab odontogram={procedure.data.odontogram} procedureId={procedureId} />
		</div>
	);
}

type OdontogramTabsProps = {
	procedureId: string;
	odontogram: {
		teeth: Teeth;
		version: number;
	} | null;
};

const OdontogramTab = memo(function Component({ odontogram, procedureId }: OdontogramTabsProps) {
	const { selectProcedure, setTeeth } = useOdontogramActions();

	useEffect(() => {
		selectProcedure(1);

		if (odontogram?.teeth) {
			setTeeth(odontogram.teeth);
		}

		return () => {
			setTeeth([]);
		};
	}, [odontogram?.teeth, setTeeth, selectProcedure]);

	return (
		<div>
			<Header procedureId={procedureId} />
			<Card className="">
				<CardContent className="">
					<div className="@container">
						<Odontogram />

						<Separator className="my-6" />

						<div className="flex flex-col items-center justify-between gap-4">
							<ProcedureSelector />
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
});

type ExpandProcedureType = {
	type: ProceduresTypesResponse;
};

type ProcedureWithType = SafeOmit<ProceduresResponse, "type"> & ExpandProcedureType;

const HeaderTitle = ({ procedureId }: { procedureId: string }) => {
	const query = pbQuery<ProcedureWithType>().expand(["type"]);

	const procedure = useQuery(
		proceduresQueries.one<ExpandProcedureType, OdontogramTabsProps["odontogram"]>({
			id: procedureId,
			options: query.build((raw, params) => pb.filter(raw, params)),
		})
	);

	return <h1 className="font-bold text-2xl">{procedure.data?.expand.type.name}</h1>;
};

const Header = ({ procedureId }: { procedureId: string }) => {
	const { undo, clear, getTeeth } = useOdontogramActions();
	const { patientId } = Route.useParams();

	const { mutate: update } = useMutation({
		mutationFn: ({ values }: { values: { odontogram: string } }) => {
			toast.loading("Updating odontogram...", {
				id: "update-odontogram",
				description: "Please wait while we update the odontogram.",
			});

			return pb.collection(Collections.Procedures).update(procedureId, values satisfies Update<Collections.Procedures>);
		},
		onSuccess: () => {
			toast.success("Odontogram updated successfully", {
				id: "update-odontogram",
				description: "The odontogram has been updated.",
			});
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, {});

			if (parsed.unknown) {
				return toast.error("Error updating odontogram", {
					id: "update-odontogram",
					// TODO: Add translation
					description: parsed.message || "An error occurred while updating the odontogram.",
				});
			}

			toast.dismiss("update-odontogram");
		},
	});

	const onSave = useCallback(() => {
		update({
			values: {
				odontogram: JSON.stringify({
					version: 1,
					teeth: getTeeth(),
				}),
			},
		});
	}, [update, getTeeth]);

	const onUndo = useCallback(() => {
		undo();
	}, [undo]);

	const onClear = useCallback(() => {
		clear();
	}, [clear]);

	return (
		<div className="mt-8 mb-2 flex flex-col items-center justify-between gap-4 md:flex-row">
			<div className="flex w-full flex-wrap justify-between gap-2">
				<Link
					className={buttonVariants({ variant: "outline" })}
					params={{ patientId }}
					to={"/dashboard/patients/$patientId/procedures"}
				>
					<ArrowLeftIcon className="h-4 w-4" />
					Volver
				</Link>

				<HeaderTitle procedureId={procedureId} />
			</div>

			<div className="flex w-full flex-wrap justify-end gap-2">
				<HelpButton />

				<Button onClick={onUndo} variant="outline">
					<UndoIcon className="h-4 w-4" />
					Deshacer
				</Button>
				<Button onClick={onClear} variant="outline">
					<TrashIcon className="size-4" />
					Limpiar
				</Button>
				<Button onClick={onSave}>
					<SaveIcon className="size-4" />
					Guardar
				</Button>
			</div>
		</div>
	);
};

const HelpButton = () => (
	<Dialog>
		<DialogTrigger asChild>
			<Button className="mb-2" size={"icon"} variant={"ghost"}>
				<HelpCircleIcon className="size-6" />
			</Button>
		</DialogTrigger>
		<DialogContent>
			<DialogHeader>
				<DialogTitle>Legenda</DialogTitle>
				<DialogDescription>
					<img alt="procedure's legend" height={600} src={proceduresLegend} width={600} />
				</DialogDescription>
			</DialogHeader>
		</DialogContent>
	</Dialog>
);
